import React, { useState } from 'react';
import { ChevronLeft, ChevronRight, Play, Pause } from 'lucide-react';

const BottomNavigation = ({
  onPreviousPOI,
  onNextPOI,
  onToggleAutoPlay,
  isAutoPlaying = false,
  isPresentationMode = false,
  currentPOI = 0,
  totalPOIs = 0
}) => {
  const [hoveredButton, setHoveredButton] = useState(null);

  const containerStyle = {
    position: 'fixed',
    bottom: '30px',
    left: '50%',
    zIndex: 1000,
    display: 'flex',
    gap: '12px',
    alignItems: 'center',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    opacity: isPresentationMode ? 0 : 1,
    pointerEvents: isPresentationMode ? 'none' : 'auto',
    transform: isPresentationMode ? 'translateX(-50%) translateY(10px)' : 'translateX(-50%)'
  };

  const buttonStyle = {
    width: '48px',
    height: '48px',
    background: 'rgba(255, 255, 255, 0.1)',
    backdropFilter: 'blur(20px)',
    border: '1px solid rgba(255, 255, 255, 0.15)',
    borderRadius: '18px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: 'rgba(255, 255, 255, 0.9)',
    transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
    position: 'relative',
    overflow: 'hidden',
    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1), inset 0 1px 0 rgba(255, 255, 255, 0.2)'
  };

  const disabledButtonStyle = {
    ...buttonStyle,
    background: 'rgba(255, 255, 255, 0.05)',
    color: 'rgba(255, 255, 255, 0.3)',
    cursor: 'not-allowed'
  };

  // Dezenter Counter unterhalb der Buttons (klein, halbtransparent, nicht im Vordergrund)
  const counterStyle = {
    position: 'absolute',
    bottom: '-22px',
    left: '50%',
    transform: 'translateX(-50%)',
    color: 'rgba(255, 255, 255, 0.45)',
    fontSize: '11px',
    fontWeight: '400',
    fontFamily: 'system-ui, -apple-system, sans-serif',
    letterSpacing: '0.04em',
    pointerEvents: 'none',
    whiteSpace: 'nowrap'
  };

  const getButtonStyle = (id, disabled = false) => {
    if (disabled) return disabledButtonStyle;
    
    const base = buttonStyle;
    
    if (hoveredButton === id) {
      return {
        ...base,
        background: 'rgba(255, 255, 255, 0.2)',
        transform: 'translateY(-3px)',
        boxShadow: '0 12px 40px rgba(0, 0, 0, 0.15), inset 0 1px 0 rgba(255, 255, 255, 0.3)',
        border: '1px solid rgba(255, 255, 255, 0.3)'
      };
    }
    return base;
  };

  const tooltipStyle = {
    position: 'absolute',
    top: '-45px',
    left: '50%',
    transform: 'translateX(-50%)',
    background: 'rgba(0, 0, 0, 0.8)',
    backdropFilter: 'blur(10px)',
    color: 'white',
    padding: '8px 12px',
    borderRadius: '10px',
    fontSize: '12px',
    fontWeight: '500',
    whiteSpace: 'nowrap',
    pointerEvents: 'none',
    opacity: 0,
    transition: 'opacity 0.2s ease',
    zIndex: 1001,
    border: '1px solid rgba(255, 255, 255, 0.1)'
  };

  const visibleTooltipStyle = {
    ...tooltipStyle,
    opacity: 1
  };

  const hasPrevious = currentPOI > 1;
  const hasNext = currentPOI < totalPOIs;

  return (
    <div style={containerStyle}>
      {/* Previous Button */}
      <button
        style={getButtonStyle('prev', !hasPrevious)}
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); if (hasPrevious) onPreviousPOI(); }}
        onDoubleClick={(e) => { e.preventDefault(); e.stopPropagation(); }}
        onMouseEnter={() => hasPrevious && setHoveredButton('prev')}
        onMouseLeave={() => setHoveredButton(null)}
        disabled={!hasPrevious}
      >
        <ChevronLeft size={20} strokeWidth={2.5} />
        <div style={hoveredButton === 'prev' ? visibleTooltipStyle : tooltipStyle}>
          Previous POI
        </div>
      </button>

      {/* Auto-Play Toggle (replaces the prominent counter) */}
      <button
        style={getButtonStyle('play')}
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); onToggleAutoPlay?.(); }}
        onMouseEnter={() => setHoveredButton('play')}
        onMouseLeave={() => setHoveredButton(null)}
        aria-label={isAutoPlaying ? 'Pause auto-play' : 'Start auto-play'}
      >
        {isAutoPlaying
          ? <Pause size={20} strokeWidth={2.5} />
          : <Play size={20} strokeWidth={2.5} style={{ marginLeft: 2 }} />}
        <div style={hoveredButton === 'play' ? visibleTooltipStyle : tooltipStyle}>
          {isAutoPlaying ? 'Pause' : 'Auto-Play'}
        </div>
        {totalPOIs > 0 && (
          <span style={counterStyle}>{currentPOI} / {totalPOIs}</span>
        )}
      </button>

      {/* Next Button */}
      <button
        style={getButtonStyle('next', !hasNext)}
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); if (hasNext) onNextPOI(); }}
        onDoubleClick={(e) => { e.preventDefault(); e.stopPropagation(); }}
        onMouseEnter={() => hasNext && setHoveredButton('next')}
        onMouseLeave={() => setHoveredButton(null)}
        disabled={!hasNext}
      >
        <ChevronRight size={20} strokeWidth={2.5} />
        <div style={hoveredButton === 'next' ? visibleTooltipStyle : tooltipStyle}>
          Next POI
        </div>
      </button>
    </div>
  );
};

export default BottomNavigation; 